"use client";

import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAction } from "next-safe-action/hooks";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";

import { trackEvent, trackMetaEvent } from "@traqly/analytics/client";
import { Button } from "@traqly/ui/components/button";
import { Input } from "@traqly/ui/components/input";
import { Label } from "@traqly/ui/components/label";
import { Separator } from "@traqly/ui/components/separator";
import { GoogleIcon } from "@traqly/ui/icons/google-icon";
import { cn } from "@traqly/ui/lib/utils";

import { signInWithGoogle } from "@actions/auth/sign-in.action";
import { signUpAction } from "@actions/auth/sign-up.action";
import {
  saveFormData,
  saveFoundingChefData,
  saveMealPrepData,
  savePlanSelection,
} from "@features/auth/utils";
import { Logo } from "@features/shared/logo";
import { signupFormSchema, type SignupFormSchema } from "@lib/schemas/auth";

const INVITE_TOKEN_CONFIG = {
  "founding-chef": {
    paramKey: "foundingToken",
    saveFn: saveFoundingChefData,
  },
  "meal-prep": {
    paramKey: "mealPrepToken",
    saveFn: saveMealPrepData,
  },
} as const;

export default function SignupForm() {
  const { executeAsync: signUp, isPending } = useAction(signUpAction);
  const searchParams = useSearchParams();
  const [metaAttribution, setMetaAttribution] = useState<{
    eventId: string;
    fbc: string;
    fbp: string;
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmContent: string;
    signupUrl: string;
  } | null>(null);
  const [googleMetaAttribution, setGoogleMetaAttribution] = useState("");

  // These browser values are intentionally captured at the user action, not
  // during render, so the event ID and fbclid timestamp stay stable per attempt.
  const captureMetaAttribution = () => {
    const eventId = `reg_${crypto.randomUUID()}`;
    // eslint-disable-next-line react-hooks/purity -- sampled on user action, not render
    const timestampMs = Date.now();
    const query = new URLSearchParams(window.location.search);
    const cookieMap = new Map(
      document.cookie.split(";").map((cookie) => {
        const [name, ...parts] = cookie.trim().split("=");
        return [name, decodeURIComponent(parts.join("="))];
      }),
    );
    const fbclid = query.get("fbclid") ?? cookieMap.get("fbclid");
    const attribution = {
      eventId,
      fbc:
        cookieMap.get("_fbc") ||
        (fbclid ? `fb.1.${timestampMs}.${fbclid}` : ""),
      fbp: cookieMap.get("_fbp") ?? "",
      utmSource: query.get("utm_source") || cookieMap.get("utm_source") || "",
      utmMedium: query.get("utm_medium") || cookieMap.get("utm_medium") || "",
      utmCampaign:
        query.get("utm_campaign") || cookieMap.get("utm_campaign") || "",
      utmContent:
        query.get("utm_content") || cookieMap.get("utm_content") || "",
      signupUrl: window.location.href,
    };
    return attribution;
  };

  useEffect(() => {
    const ref = searchParams.get("ref");
    const name = searchParams.get("name") ?? "";
    const email = searchParams.get("email") ?? "";
    const nameParts = name.trim().split(/\s+/);
    const firstName = nameParts[0] ?? "";
    const lastName = nameParts.slice(1).join(" ");

    const inviteConfig = ref
      ? INVITE_TOKEN_CONFIG[ref as keyof typeof INVITE_TOKEN_CONFIG]
      : null;
    if (inviteConfig) {
      const token = searchParams.get(inviteConfig.paramKey);
      if (token) {
        inviteConfig.saveFn({ token });
        void saveFormData({
          ...(firstName ? { firstName } : {}),
          ...(lastName ? { lastName } : {}),
          ...(email ? { businessEmail: email } : {}),
        });
      }
    }
  }, [searchParams]);

  const foundingToken = searchParams.get("foundingToken") ?? "";
  const mealPrepToken = searchParams.get("mealPrepToken") ?? "";
  const selectedPlan = searchParams.get("plan");
  const selectedBilling = searchParams.get("billing");

  useEffect(() => {
    if (
      (selectedPlan === "basics" || selectedPlan === "pro") &&
      (selectedBilling === "monthly" || selectedBilling === "annual")
    ) {
      savePlanSelection({ plan: selectedPlan, billing: selectedBilling });
    }
  }, [selectedPlan, selectedBilling]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<SignupFormSchema>({
    defaultValues: { email: "" },
    resolver: zodResolver(signupFormSchema),
  });

  const handleSignUpClick = () => {
    // Capture the sign-up initiation event before Google OAuth redirect
    trackEvent("user_signed_up", {
      auth_method: "google",
    });
  };

  async function onSubmit(values: SignupFormSchema) {
    const attribution = metaAttribution ?? captureMetaAttribution();
    setMetaAttribution(attribution);
    const result = await signUp({
      ...values,
      metaAttribution: attribution,
      ...(mealPrepToken ? { mealPrepToken } : {}),
    });

    if (result?.serverError) {
      return setError("email", { message: result.serverError });
    }
    if (result?.data?.metaLeadEvent) {
      trackMetaEvent(
        "Lead",
        {},
        { eventID: result.data.metaLeadEvent.eventId },
      );
    }
    if (result?.data?.redirectUrl) {
      window.location.assign(result.data.redirectUrl);
    }
  }

  return (
    <div
      className={cn(
        "flex w-full max-w-[526px] shrink-0 flex-col justify-center bg-white",
        "gap-5 p-6",
        "border-mono-100 rounded-3xl border",
      )}
    >
      <div className="flex flex-col items-center gap-4">
        <Logo />

        <div className="flex flex-col items-center gap-2">
          <h2 className="text-header-h1">Create your account</h2>

          <p className="text-body-large-regular text-mono-700 text-center">
            Your personal management tool for chefs
          </p>
        </div>

        <form
          className="flex w-full flex-col items-center"
          action={signInWithGoogle}
        >
          <input
            type="hidden"
            name="metaAttribution"
            value={googleMetaAttribution}
            readOnly
          />
          {foundingToken && (
            <input
              type="hidden"
              name="foundingToken"
              value={foundingToken}
            />
          )}
          {mealPrepToken && (
            <input
              type="hidden"
              name="mealPrepToken"
              value={mealPrepToken}
            />
          )}
          <Button
            variant="secondary"
            size="xl"
            type="submit"
            className="w-full gap-2 whitespace-nowrap [&_svg]:size-6"
            onClick={() => {
              handleSignUpClick();
              const attribution = captureMetaAttribution();
              setMetaAttribution(attribution);
              setGoogleMetaAttribution(JSON.stringify(attribution));
            }}
          >
            <GoogleIcon />
            Continue with Google
          </Button>
        </form>
      </div>

      <div className="text-mono-600 text-body-medium-medium flex items-center gap-4">
        <Separator className="flex-1" />
        or
        <Separator className="flex-1" />
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email Address</Label>

          <Input
            {...register("email")}
            id="email"
            inputSize="lg"
            type="email"
            autoComplete="email"
            placeholder="Email Address"
            error={errors.email?.message}
          />
        </div>

        <Button
          variant="primary"
          size="xl"
          type="submit"
          className="w-full"
          isLoading={isPending}
        >
          Send Link
        </Button>
      </form>

      <footer className="flex items-center justify-center">
        <span className="text-body-large-regular text-mono-600">
          Already have an account?
        </span>

        <Link
          // Keep the meal-prep invite token when an existing user logs in
          // instead — the login flow binds their Shopify store from it.
          href={
            mealPrepToken
              ? `/login?ref=meal-prep&mealPrepToken=${encodeURIComponent(mealPrepToken)}`
              : "/login"
          }
          className="text-button-medium text-blue-700s"
        >
          <Button
            variant="tertiary"
            size="lg"
            type="button"
          >
            Log In
          </Button>
        </Link>
      </footer>
    </div>
  );
}
